import type { Dayjs } from 'dayjs';
import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { isSameData } from '../../dto/data-model-utils.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { Price } from '../price.js';

export class GiftCardRedemption extends DataModel {
  static readonly TYPE_ID = '8c0428f836754d11833164a67a43c9e2';
  static readonly SCHEMA_VERSION = 1;

  private constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly giftCardId: string,
    public readonly templateId: string,
    public readonly orderId: string,
    public readonly amount: Price,
    public readonly creationDate: Dayjs,
  ) {
    super();
  }

  get $typeId(): string {
    return GiftCardRedemption.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(GiftCardRedemption.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string,
    businessRef: BusinessModuleRef,
    giftCardId: string,
    templateId: string,
    orderId: string,
    amount: Price,
    creationDate: Dayjs,
  }): GiftCardRedemption {
    return new GiftCardRedemption(
        args.uniqueId,
        args.businessRef.clone(),
        args.giftCardId,
        args.templateId,
        args.orderId,
        args.amount.clone(),
        args.creationDate.clone(),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GiftCardRedemption {
    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      giftCardId: MapUtils.getString(map, 'giftCardId'),
      templateId: MapUtils.getString(map, 'templateId'),
      orderId: MapUtils.getString(map, 'orderId'),
      amount: Price.decode(MapUtils.getString(map, 'amount')),
      creationDate: context.decodeDate(MapUtils.getNumber(map, 'creationDate')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId,
      businessRef: this.businessRef.encode(),
      giftCardId: this.giftCardId,
      templateId: this.templateId,
      orderId: this.orderId,
      amount: this.amount.encode(),
      creationDate: context.encodeDate(this.creationDate),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith(data: Partial<GiftCardRedemption> = {}): this {
    return GiftCardRedemption.create({ ...this, ...data }) as this;
  }

  isSame(other: this): boolean {
    return this.uniqueId === other.uniqueId &&
      isSameData(this.businessRef, other.businessRef) &&
      this.giftCardId === other.giftCardId &&
      this.templateId === other.templateId &&
      this.orderId === other.orderId &&
      isSameData(this.amount, other.amount) &&
      this.creationDate.valueOf() === other.creationDate.valueOf();
  }

  static verifyType(value: DataModel): boolean {
    return value instanceof GiftCardRedemption;
  }

  static assertType(value: DataModel): value is GiftCardRedemption {
    return value instanceof GiftCardRedemption;
  }
}

export class GiftCardRedemptionBuilder extends DataModelBuilder<GiftCardRedemption> {
  build(context: SerializationContext, map: SerializedData): GiftCardRedemption {
    return GiftCardRedemption.fromMap(context, map);
  }
}
